import { APP_TYPE, AppType } from "../../app.type";
import {
  bulkDocsByType,
  createTasks,
  getDocsByType,
  getProductTypeResponsibles
} from "../../api.service";
import { Post } from "../../../../../shared/models/post";
import { Inject, Injectable } from "@angular/core";
import { ProductTypeResponsibleServiceType } from "./product-type-responsible.service.type";
import { StringUtils } from "../../../../../shared/utils/string.utils";
import {
  ProductTypeResponsible,
  ProductTypeResponsibleRow,
  ProductTypeResponsibles
} from "../../../../../shared/models/productTypeResponsible";

/**
 * The productTypeResponsibles document is written by the import and read on case
 * creation, to give the responsibles of the product types of the part a task on the case.
 */
@Injectable()
export class ProductTypeResponsibleService
  implements ProductTypeResponsibleServiceType
{
  constructor(@Inject(APP_TYPE) private app: AppType) {}

  generateProductTypeResponsibles(rows: ProductTypeResponsibleRow[]) {
    this.app.import.docs = rows;
    rows.forEach((row) => {
      this.app.import.selected.add(row.designType);
    });

    this.app.import.stepper = "reviewList";
    this.app.import.step = "third";
    return this.app.import.docs;
  }

  getResponsiblePerProductType(rows: ProductTypeResponsibleRow[]) {
    const responsibleByProductType = new Map<string, ProductTypeResponsible>();

    rows.forEach((row) => {
      if (
        StringUtils.isNullOrEmpty(row.designType) ||
        StringUtils.isNullOrEmpty(row.responsible)
      ) {
        return;
      }

      const productTypeResponsible = new ProductTypeResponsible();
      productTypeResponsible.productType = row.designType;
      productTypeResponsible.responsible = row.responsible;
      responsibleByProductType.set(row.designType, productTypeResponsible);
    });

    return [...responsibleByProductType.values()];
  }

  /** users responsible for the product types of the part (resolved on the server) - one per user */
  async getProductTypeEngineers(partNumber: string) {
    if (StringUtils.isNullOrEmpty(partNumber)) {
      return [];
    }

    return await getProductTypeResponsibles(
      this.app.customers.expectCurrent,
      partNumber
    );
  }

  async createEngineerTasks(
    threadId: string,
    engineers: ProductTypeResponsible[]
  ) {
    const tasks: Partial<Post>[] = [];
    for (const engineer of engineers) {
      /** productType holds all the product types of the engineer, joined */
      const productTypeText = engineer.productType.includes(", ")
        ? "product types"
        : "product type";

      const task = {} as Partial<Post>;
      task.taskResponsible = engineer.responsible;
      task.taskDescription = `Please handle the case for ${productTypeText} ${engineer.productType}`;
      task.loggedInUser = this.app.state.userId;
      tasks.push(task);

      await this.app.post.setUpdatesBasedOnTaskResponsible(task);
    }

    await createTasks(this.app.customers.expectCurrent, threadId, tasks);
  }

  async saveProductTypeResponsiblesDoc(doc: ProductTypeResponsibles) {
    const oldDocs = await getDocsByType(
      this.app.customers.expectCurrent,
      "productTypeResponsibles"
    );

    /** only one document of this type is kept - delete the old one before saving the new */
    if (oldDocs.length > 0) {
      oldDocs[0]._deleted = true;
      await bulkDocsByType(
        this.app.customers.expectCurrent,
        "productTypeResponsibles",
        oldDocs
      );
    }

    await bulkDocsByType(
      this.app.customers.expectCurrent,
      "productTypeResponsibles",
      [doc]
    );
  }
}
